
import { Component, Vue } from 'vue-property-decorator'
import { Form as ElForm } from 'element-ui'
import moment from 'moment'
import { AdminApi } from '@/wwapi/apis/admin-api'
import {
  BetaGroup,
  BetaGroupMember,
  BetaGroupMemberSummary,
  getBetaGroups,
  createBetaGroup,
  updateBetaGroup,
  deleteBetaGroup,
  getBetaGroupMembers,
  addUserToBetaGroup,
  addUsersToBetaGroup,
  removeUsersFromBetaGroup,
  removeUserFromBetaGroup,
  getOtaVersionsForGroup
} from '@/api/betaGroups'

interface UserOption {
  userId: string
  label: string
}

@Component
export default class BetaGroupsPage extends Vue {
  // Groups
  private groups: BetaGroup[] = []
  private groupsLoading = false

  // Members
  private selectedGroup: BetaGroup | null = null
  private members: BetaGroupMember[] = []
  private memberSummary: BetaGroupMemberSummary | null = null
  private membersLoading = false

  // OTA Versions
  private selectedOtaGroup: BetaGroup | null = null
  private otaVersions: any[] = []
  private otaLoading = false

  // Group Dialog
  private dialogGroupVisible = false
  private dialogGroupStatus = 'create'
  private editingGroupId: number | null = null
  private groupForm = {
    groupKey: '',
    groupName: '',
    description: '',
    isDefault: false,
    isActive: true
  }
  private groupRules = {
    groupKey: [{ required: true, message: 'Group key is required', trigger: 'blur' }],
    groupName: [{ required: true, message: 'Group name is required', trigger: 'blur' }]
  }

  // Member Dialog
  private dialogAddMemberVisible = false
  private memberForm: { userIds: string[] } = { userIds: [] }
  private userOptions: UserOption[] = []
  private userSearchLoading = false
  private memberFilter = ''
  private memberStatusFilter = ''
  private selectedMembers: BetaGroupMember[] = []

  private submitLoading = false

  created() {
    this.fetchGroups()
  }

  private formatDate(date: string) {
    return moment(date).format('DD.MM.YYYY HH:mm')
  }

  // ============ Groups ============

  private async fetchGroups() {
    this.groupsLoading = true
    try {
      const res: any = await getBetaGroups()
      const data = res.data?.data || res.data
      this.groups = Array.isArray(data) ? data : []
    } catch {
      this.$message.error('Failed to fetch beta groups')
    } finally {
      this.groupsLoading = false
    }
  }

  private handleCreateGroup() {
    this.dialogGroupStatus = 'create'
    this.editingGroupId = null
    this.groupForm = { groupKey: '', groupName: '', description: '', isDefault: false, isActive: true }
    this.dialogGroupVisible = true
    this.$nextTick(() => {
      (this.$refs.groupForm as ElForm)?.clearValidate()
    })
  }

  private handleEditGroup(row: BetaGroup) {
    this.dialogGroupStatus = 'edit'
    this.editingGroupId = row.id
    this.groupForm = {
      groupKey: row.groupKey,
      groupName: row.groupName,
      description: row.description || '',
      isDefault: row.isDefault,
      isActive: row.isActive
    }
    this.dialogGroupVisible = true
    this.$nextTick(() => {
      (this.$refs.groupForm as ElForm)?.clearValidate()
    })
  }

  private submitGroup() {
    (this.$refs.groupForm as ElForm).validate(async (valid: boolean) => {
      if (!valid) return
      this.submitLoading = true
      try {
        if (this.dialogGroupStatus === 'create') {
          await createBetaGroup(this.groupForm)
          this.$message.success('Beta group created')
        } else {
          await updateBetaGroup(this.editingGroupId!, {
            groupName: this.groupForm.groupName,
            description: this.groupForm.description,
            isDefault: this.groupForm.isDefault,
            isActive: this.groupForm.isActive
          })
          this.$message.success('Beta group updated')
        }
        this.dialogGroupVisible = false
        this.fetchGroups()
      } catch (error: any) {
        this.$message.error(error.response?.data?.status?.message || 'Operation failed')
      } finally {
        this.submitLoading = false
      }
    })
  }

  private async handleDeleteGroup(row: BetaGroup) {
    try {
      await this.$confirm(`Delete group "${row.groupName}"? This cannot be undone.`, 'Warning', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      })
      await deleteBetaGroup(row.id)
      this.$message.success('Beta group deleted')
      if (this.selectedGroup?.id === row.id) this.selectedGroup = null
      if (this.selectedOtaGroup?.id === row.id) this.selectedOtaGroup = null
      this.fetchGroups()
    } catch {
      // cancelled
    }
  }

  // ============ Members ============

  private async handleViewMembers(row: BetaGroup) {
    this.selectedGroup = row
    this.selectedOtaGroup = null
    this.membersLoading = true
    try {
      const res: any = await getBetaGroupMembers(row.id)
      const body = res.data || {}
      const data = body.data || res.data
      this.members = Array.isArray(data) ? data : []
      this.memberSummary = body.summary || null
    } catch {
      this.$message.error('Failed to fetch members')
      this.memberSummary = null
    } finally {
      this.membersLoading = false
    }
  }

  // A member counts as up to date only when every cane they own is on the
  // target build. Any cane that has never reported leaves the member unknown
  // rather than silently passing or failing.
  private memberStatusLabel(row: BetaGroupMember): string {
    const devices = row.devices || []
    if (!devices.length) return 'no cane'
    if (devices.every(d => d.status === 'installed')) return 'installed'
    if (devices.some(d => d.status === 'outdated')) return 'not installed'
    return 'unknown'
  }

  private memberStatusTag(row: BetaGroupMember): string {
    const label = this.memberStatusLabel(row)
    if (label === 'installed') return 'success'
    if (label === 'not installed') return 'danger'
    return 'info'
  }

  private deviceStatusTag(status: string): string {
    if (status === 'installed') return 'success'
    if (status === 'outdated') return 'danger'
    return 'info'
  }

  private openAddMemberDialog() {
    this.memberForm.userIds = []
    this.userOptions = []
    this.dialogAddMemberVisible = true
  }

  // Remote user search for the add-member select. Reuses the proven
  // /user/list search (same one the Users screen uses) so admins can find a
  // tester by email/name instead of pasting a raw user_id. Value bound to the
  // select is users.user_id — exactly what addUserToBetaGroup expects.
  private async remoteSearchUsers(query: string) {
    const q = (query || '').trim()
    if (q.length < 2) {
      this.userOptions = []
      return
    }
    this.userSearchLoading = true
    try {
      const adminApi = new AdminApi()
      const res: any = await adminApi.userListPost({
        isPremium: null,
        isAssistant: null,
        loginType: null,
        userType: null,
        text: q,
        sort: '+id',
        page: 1,
        limit: 20
      } as any)
      const users: any[] = res.data?.data || []
      this.userOptions = users
        .filter((u) => u.userId || u.user_id)
        .map((u) => {
          const userId = u.userId || u.user_id
          const name = [u.firstName || u.first_name, u.lastName || u.last_name]
            .filter(Boolean)
            .join(' ')
            .trim()
          const email = u.email || 'no-email'
          return { userId, label: name ? `${email} (${name})` : email }
        })
    } catch {
      this.userOptions = []
      this.$message.error('User search failed')
    } finally {
      this.userSearchLoading = false
    }
  }

  private submitAddMember() {
    const userIds = (this.memberForm.userIds || []).filter(Boolean)
    if (!userIds.length) {
      this.$message.warning('Please select at least one user')
      return
    }
    this.submitLoading = true
    addUsersToBetaGroup({
      userIds,
      groupId: this.selectedGroup!.id
    }).then((res: any) => {
      // The endpoint reports per-user outcomes instead of failing the batch,
      // so surface what actually happened rather than a blanket success.
      const r = res.data?.data || {}
      const parts: string[] = []
      if (r.added?.length) parts.push(`${r.added.length} added`)
      if (r.moved?.length) parts.push(`${r.moved.length} moved from another group`)
      if (r.unchanged?.length) parts.push(`${r.unchanged.length} already in this group`)
      if (r.failed?.length) parts.push(`${r.failed.length} failed`)
      this.$message[r.failed?.length ? 'warning' : 'success'](parts.join(', ') || 'Done')
      this.dialogAddMemberVisible = false
      this.memberForm.userIds = []
      this.handleViewMembers(this.selectedGroup!)
    }).catch((error: any) => {
      this.$message.error(error.response?.data?.status?.message || 'Failed to add users')
    }).finally(() => {
      this.submitLoading = false
    })
  }

  private async handleRemoveMember(row: BetaGroupMember) {
    try {
      await this.$confirm(`Remove user "${row.userId}" from this group?`, 'Warning', {
        confirmButtonText: 'Remove',
        cancelButtonText: 'Cancel',
        type: 'warning'
      })
      await removeUserFromBetaGroup(row.userId)
      this.$message.success('User removed from group')
      this.handleViewMembers(this.selectedGroup!)
    } catch {
      // cancelled
    }
  }

  // ============ Member filtering & bulk removal ============

  // Filtering is client-side: a group is a few hundred members at most and
  // they are already loaded, so this stays instant and avoids a second
  // endpoint with its own paging semantics.
  private get filteredMembers(): BetaGroupMember[] {
    let list = this.members
    const q = this.memberFilter.trim().toLowerCase()
    if (q) {
      list = list.filter(m => {
        const name = [m.user?.firstName, m.user?.lastName].filter(Boolean).join(' ').toLowerCase()
        const serials = (m.devices || []).map(d => d.serialNumber).join(' ').toLowerCase()
        return (
          (m.user?.email || '').toLowerCase().includes(q) ||
          name.includes(q) ||
          m.userId.toLowerCase().includes(q) ||
          serials.includes(q)
        )
      })
    }
    if (this.memberStatusFilter) {
      list = list.filter(m => this.memberStatusLabel(m) === this.memberStatusFilter)
    }
    return list
  }

  private handleMemberSelectionChange(rows: BetaGroupMember[]) {
    this.selectedMembers = rows
  }

  private async handleBulkRemove() {
    if (!this.selectedMembers.length) return
    try {
      await this.$confirm(
        `Remove ${this.selectedMembers.length} user(s) from "${this.selectedGroup!.groupName}"?`,
        'Warning',
        { confirmButtonText: 'Remove', cancelButtonText: 'Cancel', type: 'warning' }
      )
      const res: any = await removeUsersFromBetaGroup({
        userIds: this.selectedMembers.map(m => m.userId)
      })
      const removed = res.data?.data?.removed ?? this.selectedMembers.length
      this.$message.success(`${removed} user(s) removed`)
      this.selectedMembers = []
      this.handleViewMembers(this.selectedGroup!)
    } catch {
      // cancelled
    }
  }

  // Adds every member currently passing the filter, so an admin can act on a
  // filtered view without ticking each row.
  private handleSelectAllFiltered() {
    const table: any = this.$refs.membersTable
    if (!table) return
    table.clearSelection()
    this.filteredMembers.forEach((row: BetaGroupMember) => table.toggleRowSelection(row, true))
  }

  // ============ OTA Versions ============

  private async handleViewOtaVersions(row: BetaGroup) {
    this.selectedOtaGroup = row
    this.selectedGroup = null
    this.otaLoading = true
    try {
      const res: any = await getOtaVersionsForGroup(row.id)
      const data = res.data?.data || res.data
      this.otaVersions = Array.isArray(data) ? data : []
    } catch {
      this.$message.error('Failed to fetch OTA versions')
    } finally {
      this.otaLoading = false
    }
  }
}
